import type { SupabaseClient } from "@supabase/supabase-js";
import { deleteRecipeAction } from "../store/actions";
import type { AppDispatch } from "../store/makeStore";

const IMAGE_BUCKET = "recipe-images";

// Extract the object path (e.g. "userId/abc.jpg") from a Supabase public Storage
// URL for the given bucket, or null if the URL isn't one of ours (e.g. a remote
// source-site URL, or empty). Strips any ?t= cache-buster.
function storagePathFromUrl(
  url: string | null | undefined,
  bucket: string,
): string | null {
  if (!url) return null;
  const marker = `/object/public/${bucket}/`;
  const i = url.indexOf(marker);
  if (i === -1) return null;
  return decodeURIComponent(url.slice(i + marker.length).split("?")[0]);
}

// Best-effort remove a recipe image from Storage given its public URL, without
// touching Redux. Used to clean up an imported (re-hosted) image when a new-recipe
// draft is cancelled before it's ever saved. No-op if the URL isn't one of ours
// (e.g. re-host failed and it's still a source-site URL).
export function removeRecipeImageByUrl(
  supabase: SupabaseClient,
  imageUrl?: string | null,
): void {
  const path = storagePathFromUrl(imageUrl, IMAGE_BUCKET);
  if (path) {
    supabase.storage
      .from(IMAGE_BUCKET)
      .remove([path])
      .catch(() => {});
  }
}

// Delete a recipe and best-effort remove its Storage image. Storage isn't
// cascade-deleted, so the image is removed here. Removes both the user-picked
// convention path (`${userId}/${recipeId}.jpg`) and whatever the recipe's current
// `image` points at if it lives in our bucket — the latter covers imported images
// re-hosted under a different name. A failure (e.g. offline) is swept up later by
// the delete-account function. `supabase` is injected so both apps reuse it.
export function deleteRecipeWithImage(
  dispatch: AppDispatch,
  supabase: SupabaseClient,
  recipeId: string,
  userId?: string,
  imageUrl?: string | null,
): void {
  dispatch(deleteRecipeAction(recipeId));
  if (!userId) return;

  const paths = new Set<string>();
  paths.add(`${userId}/${recipeId}.jpg`); // user-picked convention
  const fromUrl = storagePathFromUrl(imageUrl, IMAGE_BUCKET);
  if (fromUrl) paths.add(fromUrl); // re-hosted imported image (different name)

  supabase.storage
    .from(IMAGE_BUCKET)
    .remove([...paths])
    .catch(() => {});
}
